import React, { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { L3_PATHS } from 'Deer/constants/routes';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setIsValidateForm, useIsValidateForm } from 'reducers/reviewFormSlice';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { StyledTooltip } from 'styles/common';
import { checkNewNavEnabled, checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { sendSubmittedReviewFormEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { submitReview } from 'services/performanceReview';

import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';

import { useGoalFormData } from '../GoalForm/hooks/dataHooks';
import { useReviewFormSidebarDataOld } from '../Sidebar/hooks/dataHooks';

const SubmitFormFooter = () => {
  const [agreeConfirmation, setAgreeConfirmation] = useState(false);
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const isValidateForm = useIsValidateForm();
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();

  const history = useHistory();

  const queryClient = useQueryClient();
  const { isAllRequiredQuestionsAnswered } = queryClient.getQueryData([
    REACT_QUERY_KEYS.ReviewFormSidebar,
    employeeId,
    reviewId,
    providerId,
  ]);
  const { isPreview } = useReviewPreviewParams();
  const { data: goalFormData } = useGoalFormData({
    employeeId,
    providerId,
    reviewId,
  });

  const { data: formData = {} } = useReviewFormSidebarDataOld({
    employeeId,
    providerId,
    reviewId,
  });

  const isShowGoalWeightage = formData?.isShowGoalWeightage;

  const { okrTotalWeight = null } = goalFormData ?? {};

  let validationBasedDisable = false;
  if (isPreview) {
    validationBasedDisable = true;
  } else if (isValidateForm) {
    validationBasedDisable = !isAllRequiredQuestionsAnswered;
  }

  const goalWeightageApplicableAndNot100 =
    ['SELF', 'MANAGER'].includes(formData?.reviewInfo.providerType) &&
    okrTotalWeight !== null &&
    // eslint-disable-next-line eqeqeq
    okrTotalWeight != 100;

  const handleSubmit = () => {
    if (!isAllRequiredQuestionsAnswered) {
      return;
    }

    // redirect to compensation recommendation page only if it's a manager review
    if (checkStaticDataCompensationEnabled() && formData?.reviewInfo.providerType === 'MANAGER') {
      history.push(L3_PATHS.team.allReports.compensation.compensationRecommendation.replace(':employeeId', employeeId));
      return;
    }

    setIsSubmitInProgress(true);
    submitReview({
      reviewId,
      employeeId,
    })
      .then((res) => {
        sendSubmittedReviewFormEvent({
          eventProperties: {
            reviewCycleId: reviewId,
            empId: employeeId,
          },
        });
        queryClient.invalidateQueries(REACT_QUERY_KEYS.ReviewSections);
        const { review } = res;
        if (review.submissionStatus !== 'SUBMITTED') {
          queryClient.invalidateQueries(REACT_QUERY_KEYS.ReviewFormSidebar);
        }

        toast({
          status: 'success',
          title: 'Review submitted successfully',
        });

        if (checkNewNavEnabled()) {
          history.push(L3_PATHS['my-data'].review.myReviews);
        } else {
          history.push(routes.performanceReview.index);
        }
      })
      .finally(() => {
        setIsSubmitInProgress(false);
      });
  };

  const handleAgreeConfirmation = () => {
    dispatch(setIsValidateForm(true));
    setAgreeConfirmation((val) => !val);
  };

  const isSubmitDisabled = !agreeConfirmation || validationBasedDisable || isSubmitInProgress;

  return (
    <Flex justifyContent="space-between" bg="surface.white">
      <Checkbox isChecked={agreeConfirmation} onChange={handleAgreeConfirmation}>
        I agree that I want to submit this form and will not be able to edit it later on
      </Checkbox>
      {goalWeightageApplicableAndNot100 && isShowGoalWeightage ? (
        <StyledTooltip title="Please note that objective/goal weightages are not distributed to 100%">
          <Button disabled={isSubmitDisabled} px={6} onClick={handleSubmit}>
            Submit Review
          </Button>
        </StyledTooltip>
      ) : (
        <Button disabled={isSubmitDisabled} px={6} onClick={handleSubmit}>
          Submit Review
        </Button>
      )}
    </Flex>
  );
};

export default SubmitFormFooter;
